import { useAuth } from "@clerk/clerk-react";
import { Box, IconButton, Skeleton, Switch, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { Dispatch, SetStateAction, useContext } from "react";
import { useNavigate } from "react-router-dom";

import { fetcherAuth } from "utils/api";

interface AutopilotSettingsProps {
  pauseAll: boolean;
  setPauseAll: Dispatch<SetStateAction<boolean>>;
  loading: boolean;
}

export default function AutopilotSettings({
  pauseAll,
  setPauseAll,
  loading,
}: AutopilotSettingsProps) {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const navigate = useNavigate();

  const handleToggle = async () => {
    if (!currentOrg?.id) return;
    const newValue = !pauseAll;
    setPauseAll(newValue);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automation-settings`,
        "PUT",
        {},
        { pauseAll: newValue },
      );
    } catch (error) {
      setPauseAll(!newValue);
      setErrorAlert(error);
    }
  };

  return (
    <Box sx={{ py: 3, px: { xs: 2, sm: 3 }, maxWidth: 600 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <IconButton
          onClick={() => navigate("/dashboard/autopilot")}
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            border: "1px solid rgba(1,67,57,0.15)",
            color: "rgba(1,67,57,0.6)",
            "&:hover": {
              backgroundColor: "rgba(1,67,57,0.05)",
              color: "primary.main",
            },
          }}
        >
          <Box
            component="span"
            className="fa-solid fa-arrow-left"
            sx={{ fontSize: 14 }}
          />
        </IconButton>
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 22,
              lineHeight: "28px",
              color: "primary.main",
            }}
          >
            Automation Settings
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              lineHeight: "20px",
              color: "rgba(1,67,57,0.6)",
            }}
          >
            Manage global settings for your brand automations.
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Skeleton
          variant="rounded"
          height={100}
          sx={{ mt: 3, borderRadius: "12px" }}
        />
      ) : (
        <Box
          sx={{
            mt: 3,
            p: 2.5,
            borderRadius: "12px",
            border: "1px solid rgba(1,67,57,0.1)",
            backgroundColor: "#fafaf6",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{ fontWeight: 600, fontSize: 15, color: "primary.main" }}
            >
              Pause All Automations
            </Typography>
            <Typography
              sx={{ fontSize: 13, color: "rgba(1,67,57,0.55)", mt: 0.5 }}
            >
              When enabled, all automations will stop discovering and pitching
              new brands. Any emails already scheduled will still be delivered.
            </Typography>
          </Box>
          <Switch
            checked={pauseAll}
            onChange={handleToggle}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#014339",
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#014339",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
